import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { LanguageSwitcher } from "./LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const featureKeys = [
  { key: 'executionManagement', href: '/features/execution-management' },
  { key: 'kpiManagement', href: '/features/kpi-management' },
  { key: 'taskManagement', href: '/features/task-management' },
  { key: 'projectManagement', href: '/features/project-management' },
] as const;

const useCaseKeys = [
  { key: 'smallStartups', href: '/use-cases/small-startups' },
  { key: 'smb', href: '/use-cases/small-medium-companies' },
  { key: 'enterprise', href: '/use-cases/enterprise' },
  { key: 'itTeams', href: '/use-cases/it-teams' },
  { key: 'ecommerce', href: '/use-cases/ecommerce' },
  { key: 'saas', href: '/use-cases/saas' },
  { key: 'sales', href: '/use-cases/sales' },
  { key: 'operations', href: '/use-cases/operations' },
  { key: 'managementBoard', href: '/use-cases/management-board' },
  { key: 'publicInstitutions', href: '/use-cases/public-institutions' },
  { key: 'nonprofits', href: '/use-cases/nonprofits' },
  { key: 'consulting', href: '/use-cases/consulting' },
  { key: 'manufacturing', href: '/use-cases/manufacturing' },
] as const;

const navLinkKeys = [
  { key: 'features', href: '/#features', hasDropdown: 'features' as const },
  { key: 'useCases', href: '/use-cases', hasDropdown: 'useCases' as const },
  
  { key: 'blog', href: '/blog', hasDropdown: false as const },
  { key: 'about', href: '/about', hasDropdown: false as const },
] as const;

export function PublicNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const { t } = useTranslation();
  const { localePath } = useLanguage();

  const isActive = (href: string) => {
    const pathWithoutLang = location.pathname.replace(/^\/(en|et)/, '') || '/';
    if (href.startsWith('/#')) return pathWithoutLang === '/';
    return pathWithoutLang === href;
  };

  const getDropdownItems = (type: 'features' | 'useCases') => {
    if (type === 'features') return featureKeys.map(f => ({ key: f.key, href: f.href, label: t(`featurePages.${f.key}`) }));
    return useCaseKeys.map(u => ({ key: u.key, href: u.href, label: t(`useCases.${u.key}`) }));
  };

  const getDropdownPathPrefix = (type: 'features' | 'useCases') => {
    return type === 'features' ? '/features' : '/use-cases';
  };

  return (
    <nav className="fixed top-0 w-full z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to={localePath('/')} className="flex items-center gap-2">
            <img src="/logo-optimized.webp" alt="Outsprinter logo" width={150} height={100} className="h-10 w-auto" />
            <span className="font-bold text-xl text-foreground">Outsprinter</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinkKeys.map((link) => (
              link.hasDropdown ? (
                <DropdownMenu key={link.key}>
                  <DropdownMenuTrigger className={cn(
                    "text-sm transition-colors flex items-center gap-1 outline-none",
                    location.pathname.includes(getDropdownPathPrefix(link.hasDropdown))
                      ? "text-foreground font-medium" 
                      : "text-muted-foreground hover:text-foreground"
                  )}>
                    {t(`nav.${link.key}`)}
                    <ChevronDown className="w-3 h-3" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {getDropdownItems(link.hasDropdown).map((item) => (
                      <DropdownMenuItem key={item.key} asChild>
                        <Link to={localePath(item.href)} className="cursor-pointer">
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.key}
                  to={localePath(link.href)}
                  className={cn(
                    "text-sm transition-colors",
                    isActive(link.href) 
                      ? "text-foreground font-medium" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              )
            ))}
          </div>

          {/* Right side - Auth buttons + Language */}
          <div className="hidden lg:flex items-center gap-1">
            <LanguageSwitcher size="icon" />
            <Link to={localePath('/auth')}>
              <Button variant="ghost" size="sm">{t('nav.login')}</Button>
            </Link>
            <Link to={localePath('/auth?mode=signup')}>
              <Button size="sm">{t('nav.startFree')}</Button>
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <div className="flex items-center gap-1 lg:hidden">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-slide-down">
            <div className="flex flex-col gap-2">
              {navLinkKeys.map((link) => (
                link.hasDropdown ? (
                  <div key={link.key} className="flex flex-col">
                    <button
                      onClick={() => setMobileDropdown(mobileDropdown === link.key ? null : link.key)}
                      className={cn(
                        "px-4 py-2 text-sm rounded-md transition-colors flex items-center justify-between",
                        location.pathname.includes(getDropdownPathPrefix(link.hasDropdown))
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      )}
                    >
                      {t(`nav.${link.key}`)}
                      <ChevronDown className={cn("w-4 h-4 transition-transform", mobileDropdown === link.key && "rotate-180")} />
                    </button>
                    {mobileDropdown === link.key && (
                      <div className="flex flex-col gap-1 ml-4 mt-1">
                        {getDropdownItems(link.hasDropdown).map((item) => (
                          <Link
                            key={item.key}
                            to={localePath(item.href)}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={cn(
                              "px-4 py-2 text-sm rounded-md transition-colors",
                              location.pathname.includes(item.href)
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                            )}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.key}
                    to={localePath(link.href)}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "px-4 py-2 text-sm rounded-md transition-colors",
                      isActive(link.href)
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    {t(`nav.${link.key}`)}
                  </Link>
                )
              ))}
              <div className="flex items-center justify-center mt-2">
                <LanguageSwitcher size="sm" />
              </div>
              <div className="flex flex-col gap-2 mt-2 px-4">
                <Link to={localePath('/auth')} onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">{t('nav.login')}</Button>
                </Link>
                <Link to={localePath('/auth?mode=signup')} onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full">{t('nav.startFree')}</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
